// Workspace URL prefix: fixlr.app/<slug>/jobs → the React Router basename is "/<slug>".
// Mirrors backend tenant_slugs.RESERVED_TENANT_SLUGS — a first path segment in this list is a normal route, not a workspace.
const RESERVED = new Set([
  "a", "accept-invite", "accounts", "activity-log", "admin", "admin-messages", "ai-assistant", "api", "app", "apply", "assets",
  "best-supplier", "billing", "blog", "budgets", "calendar", "change-password", "client-approval", "client-dashboard",
  "client-portal", "clients", "compliance", "compliance-hub", "contractor", "contracts", "dashboard", "dispatch-board", "docs",
  "document-expiry", "document-templates", "emergency-jobs", "files", "fixli", "fixlr", "health-safety", "help", "hr", "in-house", "inbox", "invoices",
  "job-forms", "job-reopen-audit", "job-requests", "jobs", "knowledge-base", "live-team-map", "login", "login-poster", "logout",
  "mail", "marketing-shots", "marketplace", "messages", "modules", "my-work", "notifications", "operative-scorecard", "partner", "partners", "pay", "payments", "platform",
  "platform-dashboard", "portal", "portfolio-health", "ppm", "predictive-maintenance", "pricing", "product", "projects", "properties",
  "quote", "quotes", "re-upload", "report-issue", "report-status", "reports", "reseller", "resellers", "reset-password", "schedule",
  "settings", "setup", "share", "sign", "signup", "slas", "staff-monitoring", "staff-performance", "static", "status", "stock", "text-messages",
  "supplier", "supplier-analytics", "supplier-compare", "supplier-compliance", "supplier-onboarding",
  "supplier-outreach", "supplier-portal", "suppliers", "support", "templates", "tenant-analytics",
  "tenant-report-forms", "timeline", "track", "tracker", "uploads", "vendor", "vendor-messages", "vendor-onboard",
  "vendor-quote", "vendor-scorecard", "vendors", "verify", "verify-email", "whatsapp", "work-orders", "workspace-locked", "www",
]);
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,49}$/;

export function detectWorkspaceSlug(pathname = window.location.pathname) {
  const first = (pathname || "/").split("/")[1] || "";
  if (first.length < 3 || RESERVED.has(first) || !SLUG_RE.test(first) || first.includes(".")) return null;
  return first;
}

// Fixed for the lifetime of the page — changing prefix is always a full navigation (see WorkspaceGuard).
// `let` so resolveHostWorkspace() can switch to host-bound mode before the router mounts (ES module bindings are live).
export let WORKSPACE_SLUG = detectWorkspaceSlug();
export let WORKSPACE_BASE = WORKSPACE_SLUG ? `/${WORKSPACE_SLUG}` : "";

// Host-bound mode: a verified custom domain (portal.necos.fm) owns the whole host → that workspace, no /slug prefix.
export let HOST_SLUG = null;
export let HOST_PORTAL = null;

const PLATFORM_HOST = (() => { try { return new URL(process.env.REACT_APP_BACKEND_URL).host; } catch { return window.location.host; } })();
const HOST_CACHE_KEY = `fixli_host_ws:${window.location.host}`;
const HOST_CACHE_TTL = 10 * 60 * 1000;

export function isCustomHost() {
  return window.location.host !== PLATFORM_HOST;
}

function enterHostMode(portal) {
  HOST_SLUG = portal.slug;
  HOST_PORTAL = portal;
  if (WORKSPACE_SLUG) {
    // /<slug>/jobs on a custom domain → /jobs (any prefix is dropped: the host already says which workspace this is)
    const stripped = window.location.pathname.slice(WORKSPACE_BASE.length) || "/";
    window.history.replaceState(null, "", `${stripped}${window.location.search}${window.location.hash}`);
    WORKSPACE_SLUG = null;
    WORKSPACE_BASE = "";
  }
}

// Resolve once at boot (before the router mounts). Platform host → no-op; custom host → ask the API which workspace owns it.
export async function resolveHostWorkspace() {
  if (!isCustomHost()) return null;
  try {
    const cached = JSON.parse(sessionStorage.getItem(HOST_CACHE_KEY) || "null");
    if (cached && Date.now() - cached.at < HOST_CACHE_TTL) {
      if (cached.portal) enterHostMode(cached.portal);
      return cached.portal;
    }
  } catch { /* ignore */ }
  let portal = null;
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/tenant-portal/by-host?host=${encodeURIComponent(window.location.host)}`);
    if (res.ok) portal = await res.json();
  } catch { /* unknown host → behave like the platform host */ }
  try { sessionStorage.setItem(HOST_CACHE_KEY, JSON.stringify({ at: Date.now(), portal })); } catch { /* ignore */ }
  if (portal) enterHostMode(portal);
  return portal;
}

// Router-relative path → absolute URL path, for window.location / <a href>. <Link>/navigate() already get the prefix from the router basename.
export function wsPath(path) {
  return `${WORKSPACE_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

// Absolute URL path → router-relative path (strips the current prefix)
export function stripWorkspace(pathname) {
  if (WORKSPACE_BASE && (pathname === WORKSPACE_BASE || pathname.startsWith(`${WORKSPACE_BASE}/`))) return pathname.slice(WORKSPACE_BASE.length) || "/";
  return pathname;
}

// Prefix the signed-in user should be under: their workspace, or none for platform (master) users.
// On a custom domain nobody is prefixed — the host is the workspace (mismatches are handled by WorkspaceGuard).
export function desiredBase(user) {
  if (HOST_SLUG) return "";
  return user?.workspace_slug ? `/${user.workspace_slug}` : "";
}

// Signed-in user belongs to a different workspace than the one this custom domain serves?
export function hostMismatch(user) {
  return !!(HOST_SLUG && user?.workspace_slug && user.workspace_slug !== HOST_SLUG);
}

// Where "Back to my workspace" should go: on a custom domain that isn't theirs, the platform host's own prefix
export function ownWorkspaceUrl(user) {
  if (HOST_SLUG) return `${window.location.protocol}//${PLATFORM_HOST}${user?.workspace_slug ? `/${user.workspace_slug}` : ""}/`;
  return "/";
}

// Same prefix as the current page? (absolute path)
export function sameWorkspace(absPath) {
  return detectWorkspaceSlug(absPath) === WORKSPACE_SLUG;
}
